import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { PostDataFragment } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import initialFn from 'lodash/initial'
import lastFn from 'lodash/last'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import EmojiPicker from 'modules/core/components/Reactions/EmojiPicker'
import { EmojiBadge } from 'modules/core/components/Reactions/Reaction/EmojiBadge'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'
import { accessLegalName } from 'modules/core/lib/utils'

import * as styles from './index.vanilla.css'

type PostReaction = PostDataFragment['newReactions'][number]
type ReactionSupporter = PostReaction['supporters'][number]

interface EmojiProps {
  code: string
  count: number
  supporters: ReactionSupporter[]
}

interface ReactionProps extends EmojiProps {
  currentAccessId: number
  onClick: (code: string) => Promise<unknown>
}

const Reaction = ({ code, count, supporters, onClick, currentAccessId }: ReactionProps) => {
  const authors = useUnsafeMemo<string | null | undefined>(() => {
    const initial = compact(initialFn(supporters))
    const last = lastFn(supporters)

    if (initial.length > 0) {
      return i18n.t('common.and', {
        initial: initial.map(({ fullName }) => fullName).join(', '),
        last: last ? last.fullName : '',
      })
    }

    if (!last) {
      return ''
    }

    return last.fullName
  }, [supporters])

  const isMine = useUnsafeMemo<boolean>(
    () => !!supporters.find((supporter) => supporter.accessId === currentAccessId),
    [supporters, currentAccessId]
  )

  if (!authors) return null

  return (
    <>
      <EmojiBadge
        code={code}
        count={count}
        label={authors}
        onClick={onClick}
        selfReaction={isMine}
      />
    </>
  )
}

interface Props {
  post: PostDataFragment
}

const Reactions = ({ post }: Props) => {
  const { access } = useCurrentSession()
  const postId = post.id
  const sortedEmojis = sortBy(post.newReactions, (r) => r.code)
  const [emojis, setEmojis] = React.useState(sortedEmojis)
  const reactionStyle = styles.reactions
  const { pathname } = useLocation()

  const onClickReactions = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const { performAction: toggleEmoji } = useAsyncAction(async (code: string) => {
    const api = apiClient()
    const authorId = access.id
    const emojisNum = emojis.length
    let reaction: PostReaction | undefined
    let reactionId: number | undefined
    let reactionIndex = -1

    for (let i = 0; i < emojisNum; i++) {
      if (emojis[i]?.postId === postId && emojis[i]?.code === code) {
        reaction = emojis[i]
        reactionIndex = i
        reactionId = reaction?.supporters.find((supporter) => supporter.accessId === authorId)?.id
        break
      }
    }

    if (reactionId && reaction) {
      await api.del(`/posts/reactions/${reactionId}`)

      const newSupporters = reaction?.supporters.filter((item) => item.id !== reactionId)

      if (newSupporters && !newSupporters.length) {
        emojis.splice(reactionIndex, 1)
      } else {
        emojis[reactionIndex] = {
          ...reaction,
          count: reaction.count - 1,
          supporters: newSupporters,
        }
      }
    } else {
      const payload = {
        author_id: authorId,
        code,
        post_id: postId,
      }

      const newReaction = await api.post('/posts/reactions', payload)

      const newSupporter: ReactionSupporter = {
        id: newReaction.id,
        fullName: accessLegalName(access),
        accessId: authorId,
      }

      if (reaction) {
        emojis[reactionIndex] = {
          ...reaction,
          count: reaction.count + 1,
          supporters: [...reaction.supporters, newSupporter],
        }
      } else {
        emojis.push({
          code,
          postId: postId,
          count: 1,
          supporters: [newSupporter],
        })
      }
    }

    setEmojis(emojis)
  })

  const onEmojiSelected = async (emojiId: string) => {
    await asyncAction(() => toggleEmoji(emojiId))
  }

  return (
    <React.Suspense fallback={null}>
      <ul className={reactionStyle}>
        {emojis.map(({ code, count, supporters }: EmojiProps) => (
          <li className={reactionStyle} onClick={onClickReactions} title={code} key={code}>
            <Reaction
              code={code}
              count={count}
              supporters={supporters}
              onClick={toggleEmoji}
              currentAccessId={access.id}
            />
          </li>
        ))}
        <li className={reactionStyle} onClick={onClickReactions}>
          <EmojiPicker
            onEmojiSelected={onEmojiSelected}
            onToggle={(value) => {
              const message = value
                ? trackingEvents.communities.addReactionCancel
                : trackingEvents.communities.addReactionStart

              track(message, {
                coming_from: pathname,
                type: post.type,
              })
            }}
          />
        </li>
      </ul>
    </React.Suspense>
  )
}

export default observer(Reactions)
